import { useEffect, useState } from 'react';
import { apiClient } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { HomeStatsRead } from '../../types/api';

export interface HomeStatsResult {
  stats: HomeStatsRead | null;
  isLoading: boolean;
  error: string | null;
}

/** The last completed load, tagged with the business it belongs to. */
interface Loaded {
  businessId: string;
  stats: HomeStatsRead | null;
  error: string | null;
}

/**
 * Home's run counts: this month by status, and what is in flight now. A
 * read with no model call and no quota. Nothing gates on it; a failure only
 * blanks the tiles that use it.
 *
 * Same shape as the other Home loaders: one effect, one AbortController, the
 * result tagged by business so another brand's numbers never show.
 */
export function useHomeStats(businessId: string | null): HomeStatsResult {
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    if (!businessId) return;
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const stats = await apiClient.get<HomeStatsRead>(
          `/api/v1/businesses/${businessId}/home-stats`,
          { signal: controller.signal },
        );
        if (cancelled) return;
        setLoaded({ businessId, stats, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded({
          businessId,
          stats: null,
          error: getErrorMessage(caught, "Could not count this month's posts."),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId]);

  const isCurrent = businessId !== null && loaded?.businessId === businessId;
  return {
    stats: isCurrent ? loaded.stats : null,
    isLoading: businessId !== null && !isCurrent,
    error: isCurrent ? loaded.error : null,
  };
}
